// src/pages/DashboardLayout.js
import React, { useState } from 'react';
import {
  Box,
  VStack,
  Link,
  Text,
  Flex,
  Spacer,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Badge,
  useDisclosure,
} from '@chakra-ui/react';
import { Link as RouterLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaList, FaSignOutAlt } from 'react-icons/fa';
import usePriceData from '../hooks/usePriceData';
import { CloseIcon } from '@chakra-ui/icons';
import SubscriptionStatus from '../components/SubscriptionStatus'; // new component
import ResponsiveNav from '../components/ResponsiveNav';
import WatchlistSidebar from '../components/WatchlistSidebar';

const DashboardLayout = () => {
  const { logout, user, removeTickerFromWatchlist } = useAuth();
  const navigate = useNavigate();
  const [drawerMode, setDrawerMode] = useState('menu');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { priceData, lastUpdated } = usePriceData(user?.watchlist || []);
  const isMobileDevice = () => window.innerWidth < 768; // md breakpoint
  const location = useLocation();
  const showWatchlistButton = (() => {
    const p = location.pathname || '';
    return p.includes('/NewsAnalyzer') || p.includes('/ProfileAnalyzer');
  })();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Sidebar content used for both desktop and mobile drawer
  const SidebarContent = (
    <Box
      bg="gray.800"
      color="white"
      p={6}
      h="full"
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Text fontSize="2xl" fontWeight="bold">
          TickerMood
        </Text>
        <IconButton
          aria-label="Close sidebar"
          icon={<CloseIcon />}
          size="sm"
          display={{ base: 'inline-flex', md: 'none' }}

        />
      </Flex>



      {/* Navigation Links */}
      <VStack spacing={4} align="stretch" flex="1">
        <Link
          as={RouterLink}
          to="MarketAnalyzer"
          _hover={{ bg: 'gray.700', textDecoration: 'none' }}
          p={2}
          borderRadius="md"
          onClick={() => { if (isMobileDevice()) onClose(); }}
        >
          Market Sentiment
        </Link>

        <Link
          as={RouterLink}
          to="NewsAnalyzer"
          _hover={{ bg: 'gray.700', textDecoration: 'none' }}
          p={2}
          borderRadius="md"
          onClick={() => { if (isMobileDevice()) onClose(); }}
        >
          News Analyzer
        </Link>

        {user?.isTestUser && (
          <Link
            as={RouterLink}
            to="stock-profile-analysis"
            _hover={{ bg: 'gray.700', textDecoration: 'none' }}
            p={2}
            borderRadius="md"
            onClick={() => { if (isMobileDevice()) onClose(); }}
          >
            🧪 Profile Analyzer
          </Link>
        )}


        <Link
          as={RouterLink}
          to="ProfileAnalyzer"
          _hover={{ bg: 'gray.700', textDecoration: 'none' }}
          p={2}
          borderRadius="md"
          onClick={() => { if (isMobileDevice()) onClose(); }}
        >
          Portofolio Analyzer
        </Link>
        <Link
          as={RouterLink}
          to="watchlist"
          _hover={{ bg: 'gray.700', textDecoration: 'none' }}
          p={2}
          borderRadius="md"
          onClick={() => { if (isMobileDevice()) onClose(); }}
        >
          My Watchlist
        </Link>


        <Link
          as={RouterLink}
          to="Pricing"
          _hover={{ bg: 'gray.700', textDecoration: 'none' }}
          p={2}
          borderRadius="md"
          onClick={() => { if (isMobileDevice()) onClose(); }}
        >
          My Plan
        </Link>
        <Link
          as={RouterLink}
          to="change-password"
          _hover={{ bg: 'gray.700', textDecoration: 'none' }}
          p={2}
          borderRadius="md"
          onClick={() => { if (isMobileDevice()) onClose(); }}
        >
          Change Password
        </Link>        
      </VStack>

      <Spacer />

      {/* User Info + Subscription Badge + SubscriptionStatus + Logout */}
      <Box mt={4}>
        <Text fontSize="sm" mb={1}>
          Signed in as <strong>{user?.email}</strong>
        </Text>
        {user && <SubscriptionStatus />}
        <IconButton
          aria-label="Logout"
          icon={<FaSignOutAlt />}
          colorScheme="red"
          onClick={handleLogout}
          size="sm"
          width="100%"
          mt={2}
        />
      </Box>

    </Box>
  );

  const handleCloseDrawer = () => {
    setDrawerMode('menu');
    onClose();
  };

  return (
    <Flex direction="column" minH="100vh">
      {/* Mobile header */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        bg="gray.800"
        color="white"
        p={2}
        align="center"
      >
        <Text fontSize="lg" fontWeight="bold" flex="1">
          TickerMood
        </Text>
        {user && (
          <Badge
            colorScheme={
              user?.subscription?.plan?.toLowerCase() === 'pro'
                ? 'purple'
                : user?.subscription?.plan?.toLowerCase() === 'starter'
                  ? 'blue'
                  : 'orange' // Use orange for free so it stands out
            }
            ml={2}
          >
            {user?.subscription?.plan
              ? user.subscription.plan.charAt(0).toUpperCase() + user.subscription.plan.slice(1)
              : 'Free'}
          </Badge>

        )}
      </Flex>

      <Flex flex="1">
        {/* Desktop sidebar */}
        <Box display={{ base: 'none', md: 'block' }} w="210px">
          {SidebarContent}
        </Box>

        {/* Mobile sidebar drawer */}
        <Drawer isOpen={isOpen} placement="left" onClose={handleCloseDrawer}>
          <DrawerOverlay />
          <DrawerContent>
            <DrawerHeader>{drawerMode === 'watchlist' ? 'Watchlist' : 'Menu'}</DrawerHeader>
            <DrawerBody p={0}>
              {drawerMode === 'watchlist' ? (
                <WatchlistSidebar
                  watchlist={user?.watchlist || []}
                  priceData={priceData || {}}
                  selectedTicker={null}
                  onSelectTicker={(ticker) => {
                    // emit a global event so the active page can handle selection
                    window.dispatchEvent(new CustomEvent('watchlist-select', { detail: ticker }));
                    handleCloseDrawer();
                  }}
                  onRemoveTicker={removeTickerFromWatchlist}
                  loading={false}
                  lastUpdated={lastUpdated}
                  onClose={handleCloseDrawer}
                />
              ) : (
                SidebarContent
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        {/* Main content */}
        <Box
          flex="1"
          px={{ base: 3, md: 6 }}
          py={{ base: 4, md: 6 }}
          pb={{ base: '90px', md: 6 }}
          overflowY="auto"
          bg="gray.50"
        >
          <Box maxW="1200px" mx="auto" className="container">
            <Outlet />
          </Box>
        </Box>

        {/* Mobile bottom navigation */}
        <ResponsiveNav />

        {/* Mobile floating watchlist button (bottom-right) */}
        {showWatchlistButton && (
          <Box
            display={{ base: 'block', md: 'none' }}
            position="fixed"
            bottom={{ base: '100px', md: 'auto' }}
            right={{ base: 16, md: 'auto' }}
            zIndex={60}
          >
            <IconButton
              aria-label="Open watchlist"
              icon={<FaList />}
              colorScheme="teal"
              size="lg"
              borderRadius="full"
              onClick={() => { setDrawerMode('watchlist'); onOpen(); }}
              boxShadow="md"
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

export default DashboardLayout;
